import { useState, useEffect } from 'react'
import { supabase } from './lib/supabase'
import Auth from './screens/Auth'
import Dashboard from './screens/Dashboard'
import Calendar from './screens/Calendar'
import Clients from './screens/Clients'
import Services from './screens/Services'
import Team from './screens/Team'
import Payments from './screens/Payments'
import RenterPortal from './screens/RenterPortal'
import Profile from './screens/Profile'
import './index.css'

const NAV_ITEMS = [
  { name: 'Dashboard', icon: '◈', roles: ['owner', 'admin', 'provider'] },
  { name: 'Calendar', icon: '◎', roles: ['owner', 'admin', 'provider'] },
  { name: 'Clients', icon: '◉', roles: ['owner', 'admin'] },
  { name: 'Services', icon: '✦', roles: ['owner', 'admin'] },
  { name: 'Team', icon: '✧', roles: ['owner', 'admin'] },
  { name: 'Payments', icon: '◇', roles: ['owner', 'admin'] },
  { name: "Renter's Portal", icon: '⌂', roles: ['owner', 'admin', 'provider'] },
  { name: 'Profile', icon: '▲', roles: ['owner', 'admin', 'provider'] },
]

const SCREENS = {
  Dashboard,
  Calendar,
  Clients,
  Services,
  Team,
  Payments,
  "Renter's Portal": RenterPortal,
  Profile,
}

export default function App() {
  const [user, setUser] = useState(null)
  const [role, setRole] = useState(null)
  const [staffId, setStaffId] = useState(null)
  const [staffRecord, setStaffRecord] = useState(null)
  const [loading, setLoading] = useState(true)
  const [currentScreen, setCurrentScreen] = useState('Dashboard')

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) fetchUserRole(session.user)
      else setLoading(false)
    })

    supabase.auth.onAuthStateChange((_event, session) => {
      if (session) fetchUserRole(session.user)
      else { setUser(null); setRole(null); setLoading(false) }
    })
  }, [])

  async function fetchUserRole(authUser) {
    setUser(authUser)

    const { data: adminRoles } = await supabase
      .from('admin_roles')
      .select('role')
      .eq('profile_id', authUser.id)

    const adminRole = adminRoles?.[0]?.role || 'provider'

    const { data: staffData } = await supabase
      .from('staff')
      .select('*')
      .eq('email', authUser.email)
      .single()

    setRole(adminRole)
    setStaffId(staffData?.id || null)
    setStaffRecord(staffData || null)
    setLoading(false)
  }

  if (loading) {
    return (
      <div style={styles.loading}>
        <div style={styles.loadingLogo}>REFLECTIONS</div>
        <div style={styles.loadingBar}>
          <div style={styles.loadingBarFill} />
        </div>
      </div>
    )
  }

  if (!user) return <Auth />

  const visibleItems = NAV_ITEMS.filter(item => item.roles.includes(role || 'provider'))
  const CurrentComponent = SCREENS[currentScreen]

  return (
    <div style={styles.app}>
      {/* Sidebar */}
      <div style={styles.sidebar}>
        <div style={styles.sidebarLogo}>
          <img src="/logo.jpg" alt="Reflections" style={styles.logoImg} />
          <div style={styles.portalBadge}>STAFF PORTAL</div>
        </div>

        <nav style={styles.nav}>
          {visibleItems.map(item => (
            <button
              key={item.name}
              style={{
                ...styles.navItem,
                ...(currentScreen === item.name ? styles.navItemActive : {}),
              }}
              onClick={() => setCurrentScreen(item.name)}>
              <span style={styles.navIcon}>{item.icon}</span>
              <span style={styles.navLabel}>{item.name}</span>
              {currentScreen === item.name && (
                <div style={styles.navIndicator} />
              )}
            </button>
          ))}
        </nav>

        <div style={styles.sidebarFooter}>
          <div style={styles.userInfo}>
            <div style={styles.userAvatar}>
              {user.email?.[0]?.toUpperCase()}
            </div>
            <div style={styles.userDetails}>
              <div style={styles.userEmail}>{user.email}</div>
              <div style={styles.userRole}>{role?.toUpperCase()}</div>
            </div>
          </div>
          <button
            style={styles.signOutBtn}
            onClick={() => supabase.auth.signOut()}>
            Sign Out
          </button>
        </div>
      </div>

      {/* Main content */}
      <div style={styles.main}>
        <CurrentComponent
          role={role}
          staffId={staffId}
          staffRecord={staffRecord}
          user={user}
        />
      </div>
    </div>
  )
}

const styles = {
  loading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    backgroundColor: '#000',
    gap: '24px',
  },
  loadingLogo: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#D4AF77',
    letterSpacing: '8px',
    fontFamily: 'Cormorant Garamond, serif',
  },
  loadingBar: {
    width: '120px',
    height: '1px',
    backgroundColor: '#222',
  },
  loadingBarFill: {
    width: '60%',
    height: '100%',
    backgroundColor: '#D4AF77',
  },
  app: {
    display: 'flex',
    height: '100vh',
    overflow: 'hidden',
  },
  sidebar: {
    width: '260px',
    backgroundColor: '#000',
    borderRight: '1px solid #1a1a1a',
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
  },
  sidebarLogo: {
    padding: '24px',
    borderBottom: '1px solid #D4AF77',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
  },
  logoImg: {
    width: '160px',
    height: 'auto',
  },
  portalBadge: {
    fontSize: '9px',
    color: '#555',
    letterSpacing: '3px',
    border: '1px solid #222',
    padding: '3px 10px',
    borderRadius: '2px',
  },
  nav: {
    flex: 1,
    padding: '16px 0',
    overflowY: 'auto',
  },
  navItem: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '14px 24px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#444',
    fontSize: '12px',
    fontWeight: '600',
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
    fontFamily: 'Montserrat, sans-serif',
    position: 'relative',
    transition: 'color 0.2s',
    textAlign: 'left',
  },
  navItemActive: {
    color: '#D4AF77',
    backgroundColor: '#111',
    borderLeft: '2px solid #D4AF77',
  },
  navIcon: {
    fontSize: '16px',
    width: '20px',
    textAlign: 'center',
  },
  navLabel: {
    flex: 1,
  },
  navIndicator: {
    width: '4px',
    height: '4px',
    borderRadius: '50%',
    backgroundColor: '#D4AF77',
  },
  sidebarFooter: {
    padding: '16px',
    borderTop: '1px solid #1a1a1a',
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '12px',
  },
  userAvatar: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: '#D4AF77',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: '700',
    color: '#000',
    flexShrink: 0,
  },
  userDetails: {
    flex: 1,
    overflow: 'hidden',
  },
  userEmail: {
    fontSize: '11px',
    color: '#888',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  userRole: {
    fontSize: '9px',
    color: '#D4AF77',
    letterSpacing: '1.5px',
    marginTop: '2px',
  },
  signOutBtn: {
    width: '100%',
    padding: '10px',
    background: 'none',
    border: '1px solid #1a1a1a',
    color: '#333',
    fontSize: '10px',
    fontWeight: '600',
    letterSpacing: '2px',
    textTransform: 'uppercase',
    cursor: 'pointer',
    fontFamily: 'Montserrat, sans-serif',
    borderRadius: '2px',
  },
  main: {
    flex: 1,
    overflowY: 'auto',
    backgroundColor: '#0a0a0a',
  },
}